import type { AppView } from '../types';
import type { AppVariante } from './nativePlatform';

/**
 * Umfang der Android-App (docs/android-app-plan.md 1.4, Vertrag E5, E11).
 *
 * Die Hülle lädt die ganze Web-App. Die HANDY-Variante zeigt davon nur den
 * Begehungsteil: Dashboard, Projektliste, Projekt-Hub (nur Begehungsteil),
 * Aufgaben, Mitteilungen, Lernzentrum, Einstellungen, dazu den Material-Bereich
 * (nur mit Material-Recht). Alles andere öffnet dort den Platzhalter „Nur im
 * Browser verfügbar". Die TABLET-Variante und der
 * Browser sind unbeschränkt (voller Desktop-Umfang).
 *
 * Reine Funktionen ohne Plattform-Abfrage: die Variante reicht der Aufrufer
 * herein (`appVariante()` — Browser `null`), hier steht nur das Urteil. Die
 * Variante ist Pflicht: ein vergessenes Argument soll der Compiler finden und
 * nicht still als Handy (oder Browser) gelten.
 */

/** Greift die Umfangsgrenze? Nur in der Handy-Variante. */
export function umfangBeschraenkt(variante: AppVariante | null): boolean {
    return variante === 'handy';
}

/**
 * Urteil je Ansicht. VOLLSTÄNDIG, und der Typ erzwingt das wie `VIEW_KIND` in
 * navigationPersistence: eine neue Ansicht in `AppView` bricht hier den Build,
 * bis jemand entschieden hat, ob sie in die App gehört. Eine Positivliste
 * allein nähme jede neue Ansicht stillschweigend aus — oder, schlimmer, eine
 * Negativliste stillschweigend auf.
 */
const ANSICHT_IN_APP: Record<AppView, boolean> = {
    // ─── In der App ─────────────────────────────────────────────────────────
    dashboard: true,
    projects: true,
    tasks: true,
    lernzentrum: true,
    // Einstellungen: der Wert wird heute nirgends gerendert (Profil und
    // Einstellungen sind Dialoge); erlaubt, damit ein gemerkter Wert nicht
    // auf dem Platzhalter landet.
    settings: true,
    'project-hub': true,
    // Die Begehung selbst: Mängel, Fotos, Pläne/Pins, Checklisten,
    // Teilnehmer, Freimeldungen, Berichtsvorschau und Versand.
    'module-sigeko-inspection': true,
    // Historie/Archiv der Begehungen und der Sendeverlauf.
    'module-archive': true,
    'module-sent-reports': true,
    // Altwert der Begehung (executeModuleNavigation bildet ihn auf das
    // Prüfmodul ab); gerendert wird er nicht.
    editor: true,
    // Projektkarte: der Weg zum Projekt vor Ort (Entscheid Orchestrator
    // 26.09.2026) — öffnet nur Projekte, und die liegen in der App.
    'global-map': true,
    // Materialverwaltung (docs/materialverwaltung-plan.md): die Meldung kommt
    // als Glocke aufs Handy, „Bestellt“ und „Aufgefüllt“ setzt der Beschaffer
    // dort, wo er sie liest. Sichtbar nur mit Material-Recht (ansichtRechte).
    material: true,

    // ─── Nur im Browser ─────────────────────────────────────────────────────
    // Verwaltung (Admin-Bereiche, Nutzer, KPI, Admin-Sendeverlauf)
    admin: false,
    'admin-projects': false,
    'admin-settings': false,
    users: false,
    'project-management': false,
    // Menü-Umbau 19.10.2026: eigene Verwaltungsbereiche (adminBereiche.ts),
    // vorher Reiter der einen Admin-Konsole — ebenso Schreibtischarbeit.
    'admin-vorlagen': false,
    'admin-open-positions': false,
    'admin-substitutions': false,
    'sent-reports': false,
    // Projektmodule außerhalb der Begehung
    'module-vorank': false,
    'module-unterlage': false,
    'module-sige-plan': false,
    'module-sige-einweisung': false,
    'module-dokumente': false,
    'module-planung': false,
    'module-handbuch': false,
    'module-koordinierung': false,
    'module-asplan': false,
    'module-bauordnung': false,
    'module-sifa-unterweisung': false,
    'module-sifa-mitarbeiter': false,
    'module-sifa-fristen': false,
    'module-sifa-taetigkeiten': false,
    'module-sifa-gbu': false,
    // Vertrieb, Rechnungen, Freelancer, Steuerung
    crm: false,
    'crm-map': false,
    leads: false,
    freelancers: false,
    quotes: false,
    invoices: false,
    'erp-cockpit': false,
    // SiFa-Vorlagen (docs/sifa-vorlagen-plan.md §4): Arbeit am Schreibtisch,
    // kein Menüpunkt in der Handy-App.
    'sifa-vorlagen': false,
    // Talentpool (docs/talentpool-plan.md): Bewerbungen mit Karte sichten und
    // bearbeiten ist Arbeit am Schreibtisch — Personendaten bleiben vom Handy weg.
    talentpool: false,
};

/** Erlaubte `AppView`-Werte in der Handy-Variante (Vertrag: `ReadonlySet<string>`). */
export const APP_ANSICHTEN: ReadonlySet<string> = new Set(
    (Object.keys(ANSICHT_IN_APP) as AppView[]).filter((v) => ANSICHT_IN_APP[v]),
);

/**
 * Darf diese Ansicht gezeigt werden? Tablet und Browser: immer. Handy: nur
 * Werte aus APP_ANSICHTEN, unbekannte Werte nicht.
 */
export function ansichtInAppErlaubt(view: string, variante: AppVariante | null): boolean {
    if (!umfangBeschraenkt(variante)) return true;
    return APP_ANSICHTEN.has(view);
}

/**
 * Eingänge und Karten des Projekt-Hubs.
 *
 * Zwei Sorten Schlüssel in einer Tabelle:
 *  - die Modulschlüssel der Kachelraster (dieselben Werte, die
 *    `ProjectModuleGrid`/`SifaModuleGrid` an `onNavigate` geben und
 *    `App.executeModuleNavigation` auswertet);
 *  - die Karten, die `ProjectHub.tsx` selbst zeichnet.
 */
const HUB_IN_APP = {
    // ─── Kachelraster: Begehungsteil ────────────────────────────────────────
    editor: true,
    'module-sigeko-inspection': true,
    'project-map': true, // Standort-Auswahl (Teil der Begehung, Seitenleiste „Standort")
    'module-archive': true,
    'module-sent-reports': true,
    // ─── Kachelraster: nur im Browser ───────────────────────────────────────
    vorank: false,
    unterlage: false,
    sigeplan: false,
    handbuch: false,
    koordinierung: false,
    einweisung: false,
    dokumente: false,
    planung: false,
    asplan: false,
    bauordnung: false,
    'sifa-unterweisung': false,
    'sifa-mitarbeiter': false,
    'sifa-fristen': false,
    'sifa-taetigkeiten': false,
    'sifa-gbu': false, // Gefährdungsbeurteilungen (docs/sifa-gbu-plan.md §11)
    'sifa-angekuendigt': false, // angekündigte SiFa-Kacheln (ohne Funktion)
    // ─── Karten in ProjectHub.tsx ───────────────────────────────────────────
    begehung: true, // MobileBegehungButton
    beteiligte: true, // Projektbeteiligte & Verteiler (Teilnehmer/Kontakte)
    projektbilder: true, // Projektbilder/Referenzbild (Fotos)
    statistik: true, // Zeitplan & Intervall, Leistung & Budget (mit Buchen), Mitteilungszentrale — nicht teilbar
    uebung: true, // Übungsprojekt-Streifen (Lernzentrum)
    projektstand: true, // Laufzeit + Leistungen mit Buchung (Betreiber 28.09.2026, Vertrag E24)
    verknuepfung: false, // Mutter/Tochter, Abrechnungsart
    buchhaltung: false, // Sprung in die Rechnungen
    stammdaten: false, // Projekt-Stammdaten (nicht in E5)
    kundenzugang: false, // SiFa-Kundenbereich
    freelancer: false, // Freelancer & Begehungsaufträge
} as const satisfies Record<string, boolean>;

/** Bekannte Kennungen für `hubModulInAppErlaubt` — für Aufrufer mit Tippfehlerschutz. */
export type HubModulKennung = keyof typeof HUB_IN_APP;

/**
 * Darf dieser Hub-Eingang gezeigt werden? Tablet und Browser: immer. Handy:
 * nach Tabelle, unbekannte Kennungen nicht.
 */
export function hubModulInAppErlaubt(modulKennung: string, variante: AppVariante | null): boolean {
    if (!umfangBeschraenkt(variante)) return true;
    return Object.prototype.hasOwnProperty.call(HUB_IN_APP, modulKennung)
        && HUB_IN_APP[modulKennung as HubModulKennung];
}

// ─── Adressen ───────────────────────────────────────────────────────────────

/**
 * Basisadresse der Web-App (Muster `freimeldungLinkUrl`): die App liegt nicht
 * zwingend an der Wurzel, der Hash-Router liegt neben dem Pfad.
 */
export function appBasisAdresse(origin: string, pathname: string): string {
    return `${origin}${pathname || '/'}`;
}

/** Steht die Download-Seite `#/app` in der Adresse? */
export function istAppSeitenAdresse(hash: string): boolean {
    return /^#\/app\/?(?:\?.*)?$/.test(hash);
}

// ─── Android-Zurück-Taste ───────────────────────────────────────────────────

/** Was die Zurück-Taste gerade tun soll — in dieser Reihenfolge geprüft. */
export type ZurueckSchritt =
    | 'eingabe-verlassen'
    | 'menue-schliessen'
    | 'projektauswahl-schliessen'
    | 'statuswechsel-abbrechen'
    | 'verlauf-zurueck'
    | 'registriert'
    | 'nichts'
    | 'app-seite-schliessen'
    | 'zurueck'
    | 'minimieren';

export interface ZurueckLage {
    /** Ein Eingabefeld hat den Fokus (Tastatur evtl. schon zu). */
    eingabeFokussiert: boolean;
    /** Seitenmenü (mobil) offen. */
    menueOffen: boolean;
    /** Mobile Auswahl „Projekt wählen" offen. */
    projektauswahlOffen: boolean;
    /** Rückfrage „Projektstatus ändern?" offen. */
    statuswechselOffen: boolean;
    /** Oberstes Verlaufselement gehört einem Blatt, das per `popstate` schließt. */
    overlayImVerlauf: boolean;
    /**
     * Eine Vollbildfläche hat sich für die Zurück-Taste angemeldet
     * (registriereZurueck) UND liegt oben: kein fremder Dialog über ihrer
     * Fläche. Genau das liefert `hatRegistriertenZurueck()`.
     */
    registriert: boolean;
    /** Sonst ein Dialog/Vollbild-Overlay offen. */
    dialogOffen: boolean;
    /** Download-Seite `#/app` offen. */
    appSeiteOffen: boolean;
    /** Startseite (Dashboard) — dort gibt es kein Zurück mehr. */
    aufStartseite: boolean;
}

/**
 * Entscheidung der Zurück-Taste (Android-App) — und der Zurück-Geste im
 * Handy-Browser (Verlaufswächter, `erzeugeZurueckWaechter`). Grundsatz: nie
 * eine Eingabe verwerfen.
 *
 * Die App-eigenen Auswahlflächen (Menü, Projektauswahl, Statusrückfrage)
 * tragen keine Eingaben und schließen wie ihr Abbrechen. Ein Blatt mit eigenem
 * Verlaufseintrag (Mitteilungen) schließt über `history.back()` so, wie es die
 * Zurück-Geste im Browser tut. Eine Vollbildfläche, die sich angemeldet hat
 * (registriereZurueck), geht selbst einen Schritt zurück — so wie ihr eigener
 * Zurück-Knopf. Jeder ANDERE Dialog bleibt stehen — er kann ein halb
 * ausgefülltes Formular sein, und „Zurück" ist dort kein Speichern.
 *
 * Liegt ein fremder Dialog ÜBER der angemeldeten Fläche (z. B. ein globaler
 * Dialog der Anwendung, ein Bildeditor im Portal), hat er Vorrang: dann ist
 * `registriert` false (hatRegistriertenZurueck prüft die Fläche), und der
 * Dialog bleibt nach der Regel oben stehen — die Fläche darunter geht nicht
 * unbemerkt einen Schritt zurück.
 */
export function entscheideZurueck(lage: ZurueckLage): ZurueckSchritt {
    if (lage.eingabeFokussiert) return 'eingabe-verlassen';
    if (lage.menueOffen) return 'menue-schliessen';
    if (lage.projektauswahlOffen) return 'projektauswahl-schliessen';
    if (lage.statuswechselOffen) return 'statuswechsel-abbrechen';
    if (lage.overlayImVerlauf) return 'verlauf-zurueck';
    if (lage.registriert) return 'registriert';
    if (lage.dialogOffen) return 'nichts';
    if (lage.appSeiteOffen) return 'app-seite-schliessen';
    if (lage.aufStartseite) return 'minimieren';
    return 'zurueck';
}

/**
 * Gehört der oberste Verlaufseintrag einem Blatt der Anwendung? Diese Blätter
 * (`NotificationSheet`: `{ osiaSheet: … }`) legen beim Öffnen einen Eintrag
 * und schließen bei `popstate`. Der Verlaufswächter (`WAECHTER_SCHLUESSEL`)
 * beginnt bewusst nicht mit „osia" und zählt nicht dazu.
 */
export function istOverlayVerlauf(state: unknown): boolean {
    if (!state || typeof state !== 'object') return false;
    return Object.keys(state).some((k) => k.startsWith('osia'));
}

/** Minimal, damit sich die DOM-Abfragen ohne Browser prüfen lassen. */
export interface FokusElement {
    tagName: string;
    isContentEditable?: boolean;
    getAttribute?: (name: string) => string | null;
}

const KEINE_TEXTEINGABE = new Set(['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'color', 'file', 'image', 'hidden']);

/** Hat ein Texteingabefeld den Fokus? Knöpfe, Häkchen u. ä. zählen nicht. */
export function istEingabeFokussiert(el: FokusElement | null | undefined): boolean {
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = el.tagName.toUpperCase();
    if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (tag !== 'INPUT') return false;
    const typ = (el.getAttribute?.('type') || 'text').toLowerCase();
    return !KEINE_TEXTEINGABE.has(typ);
}

/**
 * Selektor für Dialoge und Vollbild-Overlays. Die meisten Dialoge der
 * Anwendung sind `fixed inset-0`-Flächen ohne `role`, deshalb zählt die
 * Klasse mit.
 */
export const DIALOG_SELEKTOR = '[role="dialog"], [aria-modal="true"], dialog[open], .fixed.inset-0';

export interface DialogElement {
    getClientRects(): { length: number };
    /** DOM `Node.contains` — nur für den Abgleich mit einer angemeldeten Fläche. */
    contains?(anderes: unknown): boolean;
}

export interface DialogDokument {
    querySelectorAll(selektor: string): ArrayLike<DialogElement>;
    defaultView: { getComputedStyle(el: DialogElement): { visibility: string; pointerEvents: string } } | null;
}

/**
 * Steht gerade ein Dialog oder Vollbild-Overlay auf dem Schirm?
 *
 * Ausgeblendete Flächen (`display: none`, z. B. `md:hidden`) haben keine
 * Layout-Rechtecke, unsichtbare zählen nicht, und Flächen mit
 * `pointer-events: none` (Tour-Overlay) sind kein Dialog.
 *
 * `ausser`: Flächen angemeldeter Vollbildansichten. Sie selbst, alles IN
 * ihnen (ihre eigenen Overlays regelt ihr Handler) und die Flächen, in denen
 * sie liegen, zählen dann nicht — gefragt ist nur nach FREMDEN Dialogen.
 */
export function offenerDialogImDom(doc: DialogDokument, ausser: ReadonlyArray<DialogElement | null> = []): boolean {
    const flaechen = ausser.filter((f): f is DialogElement => f !== null);
    const elemente = doc.querySelectorAll(DIALOG_SELEKTOR);
    for (let i = 0; i < elemente.length; i++) {
        const el = elemente[i];
        if (flaechen.some((f) => gehoertZurFlaeche(el, f))) continue;
        if (el.getClientRects().length === 0) continue;
        const stil = doc.defaultView?.getComputedStyle(el);
        if (stil && (stil.visibility === 'hidden' || stil.pointerEvents === 'none')) continue;
        return true;
    }
    return false;
}

/** Ist `el` die Fläche selbst, liegt in ihr oder umschließt sie? */
function gehoertZurFlaeche(el: DialogElement, flaeche: DialogElement): boolean {
    return el === flaeche || flaeche.contains?.(el) === true || el.contains?.(flaeche) === true;
}

// ─── Zurück-Taste in Vollbildflächen (Opt-in) ───────────────────────────────

/**
 * Stapel der angemeldeten Zurück-Handler, zuletzt angemeldet = oben (LIFO).
 *
 * Vollbildflächen (`fixed inset-0`) gelten der Zurück-Taste sonst als Dialog
 * und bleiben stehen. Wer einen eigenen, verlustfreien Zurück-Weg hat (der
 * Mängel-Assistent: ein Schritt zurück), meldet ihn hier an und bekommt den
 * Tastendruck. Reine Betrachter melden nichts an und bleiben wie bisher stehen.
 * Im Handy-Browser liest ihn der Verlaufswächter ebenso (Zurück-Geste), am
 * Desktop niemand.
 */
const zurueckStapel: Array<{ handler: () => void; flaeche?: () => DialogElement | null }> = [];

/**
 * Handler anmelden; der Rückgabewert meldet genau diesen wieder ab (mehrfach aufrufbar).
 *
 * `flaeche` liefert das Wurzelelement der Vollbildansicht (z. B. `() => ref.current`).
 * Damit erkennt `hatRegistriertenZurueck`, ob ein FREMDER Dialog offen ist
 * — der hat dann Vorrang. Ohne `flaeche` bleibt es beim alten Verhalten
 * (der Handler bekommt den Druck, egal was sonst offen ist).
 */
export function registriereZurueck(handler: () => void, flaeche?: () => DialogElement | null): () => void {
    const eintrag = { handler, flaeche };
    zurueckStapel.push(eintrag);
    return () => {
        const i = zurueckStapel.lastIndexOf(eintrag);
        if (i >= 0) zurueckStapel.splice(i, 1);
    };
}

function globalesDokument(): DialogDokument | null {
    return typeof document === 'undefined' ? null : document;
}

/**
 * Soll der oberste angemeldete Handler den Druck bekommen?
 *
 * Nein, wenn keiner angemeldet ist — und nein, wenn AUSSERHALB der
 * angemeldeten Flächen ein Dialog offen ist (`offenerDialogImDom(doc,
 * flaechen)`): der liegt über ihnen (globaler Dialog, Portal) und hat Vorrang.
 * Die Flächen tiefer im Stapel zählen nicht als fremd, sie liegen unter der
 * obersten (LIFO). Einfach und auf der sicheren Seite: auch ein fremder
 * Dialog UNTER der Fläche hielte die Taste an — dann bleibt sie stehen wie vor
 * der Anmeldung, es geht nichts verloren.
 *
 * Liefert die Fläche (noch) kein Element, zählt jeder andere offene Dialog als fremd.
 * `doc`: ohne Angabe das Dokument der Seite; `null` = ohne DOM-Prüfung.
 */
export function hatRegistriertenZurueck(doc: DialogDokument | null = globalesDokument()): boolean {
    const oben = zurueckStapel[zurueckStapel.length - 1];
    if (!oben) return false;
    if (!oben.flaeche || !doc) return true;
    return !offenerDialogImDom(doc, zurueckStapel.map((e) => e.flaeche?.() ?? null));
}

/** Den obersten Handler ausführen. false, wenn keiner angemeldet ist. */
export function registriertenZurueckAusfuehren(): boolean {
    const oben = zurueckStapel[zurueckStapel.length - 1];
    if (!oben) return false;
    oben.handler();
    return true;
}

// ─── Zurück-Geste im Handy-Browser (Verlaufswächter) ────────────────────────
//
// Im Browser gibt es keine Zurück-Taste, die die Anwendung abfangen könnte:
// die Geste (Android ohne App, Wischen auf dem iPhone) geht im Verlauf zurück
// und verließ bisher die Seite, auch mitten im Prüfmodul. Der Wächter legt
// deshalb, solange eine Rückkehr IN der Anwendung möglich ist, genau EINEN
// eigenen Verlaufseintrag oben auf. Nimmt die Geste ihn weg (`popstate`),
// trifft die Anwendung dieselbe Entscheidung wie für die Android-Taste
// (`entscheideZurueck`) und betritt den Eintrag danach wieder, wenn weiter
// eine Rückkehr möglich ist.
//
// Wiederbetreten heißt `history.forward()`, nicht ein neues `pushState`. Beide
// großen Browser überspringen bei der Zurück-GESTE Einträge, die eine Seite
// ohne frische Nutzerhandlung angelegt hat. Chromium („history manipulation
// intervention", im Chromium-Quelltext
// docs/history_manipulation_intervention.md) markiert dann ALLE Einträge des
// Dokuments, und nach einer Verlaufsbewegung zählt es eine frühere Berührung
// für neue Einträge nicht mehr. WebKit markiert Einträge ohne Berührung in den
// letzten zehn Sekunden (`wasCreatedByJSWithoutUserInteraction`) — einen
// markierten Wächter überspringt die Geste und verlässt die Seite. Ein sofort
// neu gelegter Wächter ließe also schon die NÄCHSTE Geste die Anwendung
// verlassen, genau auf dem Weg Modul → Hub → Dashboard. Ein Vorwärtsschritt
// legt keinen Eintrag an. Neue Einträge entstehen nur nach einer frischen
// Berührung (`eintragErlaubt`); ohne sie wartet der Wächter auf die nächste.

/**
 * Schlüssel des Wächters im Verlaufszustand. Beginnt bewusst NICHT mit
 * „osia": `istOverlayVerlauf` hielte den Eintrag sonst für ein Blatt, und
 * `entscheideZurueck` wählte 'verlauf-zurueck'.
 */
export const WAECHTER_SCHLUESSEL = 'zurueckWaechter';

/**
 * Markiert einen übernommenen Wächter (`waechterArt` 'geerbt'): unter ihm liegt
 * ein Eintrag eines früheren Ladens. Chromium (geprüft) und WebKit (Quelltext:
 * Neuladen behält die documentSequenceNumber) gehen dorthin ohne Neuladen; sicher
 * ist das nicht überall, deshalb räumt der Wächter ihn nie selbst per back() ab.
 */
export const WAECHTER_GEERBT_SCHLUESSEL = 'zurueckWaechterGeerbt';

/** So alt darf eine Berührung sein, damit ein neuer Eintrag als Nutzerhandlung gilt (WebKit: zehn Sekunden). */
export const AKTIVIERUNG_GILT_MS = 10_000;

/**
 * So lange wartet der Wächter höchstens auf das `popstate` seiner eigenen
 * Verlaufsbewegung. Gewöhnlich kommt es nach Millisekunden; großzügig, damit
 * es auch bei belegtem Hauptfaden nicht als Geste gilt.
 */
export const WAECHTER_BEWEGUNG_MS = 1_500;

/**
 * Kennung je Seitenaufruf. Der Verlaufszustand überlebt ein Neuladen (F5,
 * Update, Sitzungsende): ein Wächter aus einem früheren Laden ist nicht der
 * eigene — er wird übernommen (`waechterArt` 'frueher' → 'uebernehmen').
 */
export function neueWaechterKennung(zufall: () => number = Math.random, jetzt: () => number = Date.now): string {
    return `${jetzt().toString(36)}-${zufall().toString(36).slice(2, 10)}`;
}

/**
 * Welcher Wächter liegt in diesem Verlaufszustand? 'eigen': von diesem
 * Seitenaufruf gelegt. 'geerbt': aus einem früheren Laden übernommen.
 * 'frueher': aus einem früheren Laden, noch nicht übernommen. null: keiner.
 */
export type WaechterArt = 'eigen' | 'geerbt' | 'frueher';

export function waechterArt(state: unknown, kennung: string): WaechterArt | null {
    if (!state || typeof state !== 'object') return null;
    const felder = state as Record<string, unknown>;
    const wert = felder[WAECHTER_SCHLUESSEL];
    if (typeof wert !== 'string' || wert === '') return null;
    if (wert !== kennung) return 'frueher';
    return felder[WAECHTER_GEERBT_SCHLUESSEL] === true ? 'geerbt' : 'eigen';
}

/** Ist dieser Verlaufszustand der Wächter DIESES Seitenaufrufs (gelegt oder übernommen)? */
export function istWaechterEintrag(state: unknown, kennung: string): boolean {
    const art = waechterArt(state, kennung);
    return art === 'eigen' || art === 'geerbt';
}

/**
 * Zustand des Wächters: der bisherige Zustand (fremde Schlüssel bleiben
 * erhalten) plus Kennung, beim Übernehmen mit Erbe-Merker. Schlüssel eines
 * Blatts („osia…") wandern NICHT mit — ein Wächter darf nie wie ein Blatt
 * aussehen —, und ein frisch gelegter trägt keinen alten Erbe-Merker.
 */
export function waechterZustand(state: unknown, kennung: string, geerbt = false): Record<string, unknown> {
    const zustand: Record<string, unknown> = {};
    if (state && typeof state === 'object' && !Array.isArray(state)) {
        for (const [schluessel, wert] of Object.entries(state)) {
            if (schluessel.startsWith('osia') || schluessel === WAECHTER_GEERBT_SCHLUESSEL) continue;
            zustand[schluessel] = wert;
        }
    }
    zustand[WAECHTER_SCHLUESSEL] = kennung;
    if (geerbt) zustand[WAECHTER_GEERBT_SCHLUESSEL] = true;
    return zustand;
}

/**
 * Braucht es den Wächter? Ja, solange die Zurück-Geste IN der Anwendung etwas
 * tut — alles außer 'minimieren' (Startseite, nichts offen). Abseits der
 * Startseite also immer. Ein fokussiertes Feld zählt hier nicht: der Fokus
 * wechselt ohne Neuzeichnen, der Wächter läge sonst zufällig. Bei der Geste
 * selbst entscheidet `entscheideZurueck` mit Fokus (erst das Feld verlassen).
 */
export function waechterNoetig(lage: ZurueckLage): boolean {
    return entscheideZurueck({ ...lage, eingabeFokussiert: false }) !== 'minimieren';
}

/** Ein Verlaufseintrag, wie ihn der Wächter sieht. */
export interface VerlaufsStand {
    state: unknown;
    /** `location.href` */
    adresse: string;
}

/**
 * Hat dieses `popstate` den eigenen Wächter weggenommen?
 *
 * Nur, wenn vorher der eigene Wächter oben lag, jetzt nicht mehr — und die
 * Adresse gleich blieb: Wächter und Eintrag darunter tragen dieselbe Adresse
 * (pushState ohne URL). Ein `popstate` mit neuer Adresse kommt von einer
 * Hash-Navigation (`#/app` legt einen Eintrag ÜBER den Wächter, Chromium
 * meldet auch das als popstate) und ist keine Zurück-Geste.
 *
 * Wurde ein fremder Eintrag über dem Wächter entnommen (Blatt), liegt danach
 * der Wächter oben: nichts zu tun, das Blatt schließt sich selbst.
 */
export function waechterEntnommen(vorher: VerlaufsStand | null, nachher: VerlaufsStand, kennung: string): boolean {
    if (!vorher) return false;
    return istWaechterEintrag(vorher.state, kennung)
        && !istWaechterEintrag(nachher.state, kennung)
        && vorher.adresse === nachher.adresse;
}

/** Letzte Berührung (Tippen, Taste) — zählt ein neuer Eintrag als Nutzerhandlung? */
export interface Aktivierung {
    zeitMs: number | null;
    /** Für einen Eintrag verbraucht oder durch eine Verlaufsbewegung entwertet. */
    verbraucht: boolean;
}

/**
 * Darf der Wächter jetzt einen NEUEN Eintrag anlegen? Nur mit einer frischen
 * Berührung: Chromium verbraucht sie je Eintrag und entwertet sie bei jeder
 * Verlaufsbewegung, WebKit lässt sie zehn Sekunden gelten. Ohne sie wartet der
 * Wächter auf die nächste Berührung — ein übersprungener Wächter wäre
 * schlimmer als keiner: er bliebe markiert, und jede Geste von ihm verließe
 * die Seite.
 */
export function eintragErlaubt(aktivierung: Aktivierung, jetztMs: number): boolean {
    return aktivierung.zeitMs !== null
        && !aktivierung.verbraucht
        && jetztMs - aktivierung.zeitMs <= AKTIVIERUNG_GILT_MS;
}

/**
 * Liegt oben ein fremder Eintrag, der die Geste selbst fängt? Ein Blatt
 * (`osia…`, schließt bei popstate) oder die Download-Seite `#/app`: selbst
 * geöffnet trägt sie einen eigenen Hash-Eintrag, von außen geöffnet ist sie
 * eine Landeseite, von der die Geste wie bisher wegführt. Darüber legt der
 * Wächter nichts — kein Stapel, und ein Wächter mit `#/app`-Adresse öffnete
 * die Seite beim Wiederbetreten erneut.
 */
export function fremderVerlaufseintragOben(state: unknown, hash: string): boolean {
    return istOverlayVerlauf(state) || istAppSeitenAdresse(hash);
}

export interface WaechterLage {
    /** Eine Rückkehr in der Anwendung ist möglich (`waechterNoetig`). */
    noetig: boolean;
    /** Welcher Wächter ist der aktuelle Eintrag (`waechterArt`)? */
    oben: WaechterArt | null;
    /** Ein fremder Eintrag mit eigener Zurück-Behandlung liegt oben. */
    fremderEintragOben: boolean;
    /** Der eben entnommene Wächter liegt direkt vorwärts und darf wieder betreten werden. */
    vorwaertsMoeglich: boolean;
    /** Ein neuer Eintrag zählt als Nutzerhandlung (`eintragErlaubt`). */
    eintragErlaubt: boolean;
    /**
     * Ein eigenes back() blieb ohne popstate — vielleicht kommt es noch. Bis
     * zum nächsten popstate kein weiteres: ein zweites liefe womöglich ohne
     * Geste aus der Anwendung hinaus.
     */
    rueckschrittGesperrt: boolean;
}

/**
 * Was der Wächter jetzt tun soll. 'uebernehmen': einen Wächter aus einem
 * früheren Laden per replaceState zum eigenen machen — kein neuer Eintrag,
 * deshalb ohne Berührung, und nichts häuft sich über mehrere Neuladen an.
 */
export type WaechterSchritt = 'legen' | 'uebernehmen' | 'vorwaerts' | 'abraeumen' | 'nichts';

/**
 * Ein Abgleich. Nie zwei Wächter übereinander: gelegt wird nur, wenn keiner
 * oben liegt, ein früherer wird übernommen, und nach einer Geste wird der alte
 * Eintrag wieder betreten statt ein neuer angelegt. Ohne Rückkehrmöglichkeit
 * wird ein selbst gelegter Wächter abgeräumt (`history.back()`), damit die
 * nächste Geste die Seite verlässt statt ins Leere zu gehen; ein geerbter nie
 * (siehe WAECHTER_GEERBT_SCHLUESSEL) — dort kostet es höchstens eine Geste.
 *
 * `noetig` wird zuletzt gelesen und nur, wenn es etwas ändert: auf der
 * Startseite kostet es DOM-Abfragen (der Wächter reicht es als Getter herein),
 * und ohne Wächter oben, ohne Rückkehrerlaubnis und ohne frische Berührung
 * bleibt es ohnehin bei 'nichts'.
 */
export function waechterSchritt(lage: WaechterLage): WaechterSchritt {
    if (lage.oben === 'eigen') {
        if (lage.rueckschrittGesperrt) return 'nichts';
        return lage.noetig ? 'nichts' : 'abraeumen';
    }
    if (lage.oben === 'geerbt') return 'nichts';
    if (lage.oben === 'frueher') return lage.noetig ? 'uebernehmen' : 'nichts';
    if (lage.fremderEintragOben) return 'nichts';
    if (!lage.vorwaertsMoeglich && !lage.eintragErlaubt) return 'nichts';
    if (!lage.noetig) return 'nichts';
    return lage.vorwaertsMoeglich ? 'vorwaerts' : 'legen';
}

/**
 * Zählt dieses Ereignis für den Browser als Nutzerhandlung? Nur echte
 * (`isTrusted`; ein `a.click()` im Code zählt nicht) und nicht Escape.
 */
export function zaehltAlsBeruehrung(ereignis: { readonly isTrusted?: boolean; readonly key?: string }): boolean {
    return ereignis.isTrusted !== false && ereignis.key !== 'Escape';
}

/** Zugriff auf den Verlauf — `window.history` oder im Test eine Attrappe. */
export interface VerlaufsZugriff {
    readonly state: unknown;
    pushState(data: unknown, unused: string): void;
    replaceState(data: unknown, unused: string): void;
    back(): void;
    forward(): void;
}

/** Was die Oberfläche dem Wächter nach jedem Zeichnen mitgibt. */
export interface WaechterOberflaeche {
    /** Startseite (Dashboard)? Abseits davon ist eine Rückkehr immer möglich. */
    aufStartseite: boolean;
    /** Volle Lage wie für die Android-Taste — mit DOM-Abfragen, deshalb nur auf der Startseite gefragt. */
    lage(): ZurueckLage;
    /** Die Entscheidung ausführen — derselbe Weg wie die Android-Taste. */
    zurueck(): void;
}

export interface WaechterUmgebung {
    verlauf: VerlaufsZugriff;
    /** `location.href` */
    adresse(): string;
    /** `location.hash` */
    hash(): string;
    jetzt(): number;
    planen(fn: () => void, ms: number): number;
    abbrechen(handle: number): void;
    /** null: der Wächter ruht (keine volle Oberfläche — Anmeldung, Ladeschirm, Bauphase, Freelancer-Portal). */
    oberflaeche(): WaechterOberflaeche | null;
    /** `neueWaechterKennung()` — einmal je Seitenaufruf, nicht je Wächter. */
    kennung: string;
}

export interface ZurueckWaechter {
    /** Nach jedem Zeichnen: Wächter legen, übernehmen oder abräumen. */
    abgleichen(): void;
    /** `popstate`: war es der eigene Wächter, die Entscheidung ausführen. */
    beiPopstate(): void;
    /** Berührung oder Taste — es zählt nur, was `zaehltAlsBeruehrung` gelten lässt. */
    beiBeruehrung(ereignis?: { readonly isTrusted?: boolean; readonly key?: string }): void;
    /** Zeitgeber aufräumen; danach tut der Wächter nichts mehr. */
    beenden(): void;
}

/**
 * Der Wächter im Ablauf. Ohne DOM-Zugriff: Verlauf, Uhr, Zeitgeber und
 * Oberfläche kommen herein (im Test eine Attrappe).
 *
 * Ablauf einer Geste: `popstate` nimmt den Wächter → Entscheidung
 * (`oberflaeche.zurueck`) → React zeichnet → der nächste Abgleich aus dem
 * ZEITGEBER betritt den Wächter wieder (`forward`), wenn noch eine Rückkehr
 * möglich ist. Erst der Zeitgeber, nicht schon das Zeichnen: dann ist die
 * Entscheidung gezeichnet, und hat sie selbst den Verlauf bewegt, hat dessen
 * `popstate` die Rückkehrerlaubnis schon gelöscht. Das `popstate` der eigenen
 * Bewegung ist keine Geste. Fehlt bei der Geste die volle Oberfläche
 * (Ladeschirm), fällt keine Entscheidung; der Wächter wird wieder betreten,
 * sobald sie zurück ist — die Geste bleibt ohne Wirkung, statt dass die
 * nächste die Seite verlässt.
 *
 * Bewusste Grenzen:
 * - Ruht der Wächter (Anmeldung, Ladeschirm, Bauphase) oder wird er abgebaut
 *   (Drehen über die Mobilgrenze, Abmelden), bleibt ein oben liegender Eintrag
 *   stehen; die nächste Geste nimmt ihn ohne Entscheidung weg. Kein back()
 *   beim Abbau: es liefe mit dem nächsten Aufbau ins Rennen.
 * - Hat ein anderer den Verlauf bewegt (Blatt per Kreuz geschlossen, dabei
 *   ein Projekt geöffnet: „Mitteilung → Projekt"), kommt ein neuer Wächter
 *   erst mit der nächsten Berührung — Chromium zählte ihn sonst als
 *   übersprungen.
 * - Schließt die Anwendung ihre `#/app`-Seite zweimal kurz hintereinander
 *   (Ansichtswechsel und Effekt), kann der zweite Aufruf den Hash-Eintrag per
 *   replaceState leeren, bevor das back() des ersten ankommt. Danach liegt
 *   der Wächter (falls einer darunter lag) oben, und vorwärts bleibt ein
 *   leerer Eintrag — kein Stapel, keine Fehlentscheidung.
 * - F5 auf der `#/app`-Seite und danach ihr eigener Zurück-Knopf: der
 *   Hash-Eintrag wird per replaceState zur Basisadresse, der alte Wächter liegt
 *   DARUNTER und lässt sich nicht übernehmen (der Wächter sieht nur den
 *   obersten Eintrag). Die nächste Berührung legt einen neuen — das Verlassen
 *   kostet später zwei Gesten mehr. Mit der Geste statt des Knopfs nicht.
 * - Kommt das popstate des eigenen Abräumens erst nach Ablauf der Frist
 *   (Hauptfaden blockiert), gilt es als Geste. Abgeräumt wird nur auf der
 *   Startseite ohne offene Fläche — die Entscheidung ist dort „minimieren“,
 *   also nichts.
 */
export function erzeugeZurueckWaechter(u: WaechterUmgebung): ZurueckWaechter {
    const stand = (): VerlaufsStand => ({ state: u.verlauf.state, adresse: u.adresse() });
    let letzter: VerlaufsStand = stand();
    let aktivierung: Aktivierung = { zeitMs: null, verbraucht: true };
    /** Eintrag, auf dem die letzte Geste landete — der Wächter liegt direkt vorwärts. */
    let gelandet: VerlaufsStand | null = null;
    /** Eigene Verlaufsbewegung unterwegs: welche, und bis wann ihr popstate erwartet wird. */
    let bewegung: { art: 'vorwaerts' | 'abraeumen'; bis: number } | null = null;
    let rueckschrittGesperrt = false;
    let zeitgeber: number | null = null;
    let beendet = false;

    const planeAbgleich = (ms: number): void => {
        if (beendet) return;
        if (zeitgeber !== null) u.abbrechen(zeitgeber);
        zeitgeber = u.planen(() => {
            zeitgeber = null;
            abgleich(true);
        }, ms);
    };

    const abgleich = (ausZeitgeber: boolean): void => {
        if (beendet) return;
        const jetzt = u.jetzt();
        if (bewegung !== null) {
            if (jetzt < bewegung.bis) {
                planeAbgleich(bewegung.bis - jetzt);
                return;
            }
            // Kein popstate gekommen. Ein back() nicht wiederholen: kommt sein
            // popstate doch noch, ginge ein zweites einen Eintrag zu weit.
            if (bewegung.art === 'abraeumen') rueckschrittGesperrt = true;
            bewegung = null;
        }
        const aktuell = stand();
        letzter = aktuell;
        const oberflaeche = u.oberflaeche();
        // Ohne volle Oberfläche ruht er; eine offene Rückkehr bleibt liegen.
        if (!oberflaeche) return;
        const zurueckGelandet = ausZeitgeber ? gelandet : null;
        if (ausZeitgeber) gelandet = null;
        // Die Rückkehr nur aus dem Zeitgeber (s. o.): steht eine an, ihn anstoßen.
        else if (gelandet !== null && zeitgeber === null) planeAbgleich(0);
        const schritt = waechterSchritt({
            // Erst bei Bedarf: auf der Startseite fragt die Lage das DOM ab.
            get noetig() { return !oberflaeche.aufStartseite || waechterNoetig(oberflaeche.lage()); },
            oben: waechterArt(aktuell.state, u.kennung),
            fremderEintragOben: fremderVerlaufseintragOben(aktuell.state, u.hash()),
            // Hat die Entscheidung den Eintrag ersetzt oder die Adresse
            // geändert, liegt vorwärts womöglich nicht mehr der Wächter.
            vorwaertsMoeglich: zurueckGelandet !== null
                && zurueckGelandet.state === aktuell.state
                && zurueckGelandet.adresse === aktuell.adresse,
            eintragErlaubt: eintragErlaubt(aktivierung, jetzt),
            rueckschrittGesperrt,
        });
        try {
            switch (schritt) {
                case 'legen':
                    u.verlauf.pushState(waechterZustand(aktuell.state, u.kennung), '');
                    aktivierung = { ...aktivierung, verbraucht: true };
                    letzter = stand();
                    break;
                case 'uebernehmen':
                    // Kein neuer Eintrag: braucht keine Berührung und verbraucht keine.
                    u.verlauf.replaceState(waechterZustand(aktuell.state, u.kennung, true), '');
                    letzter = stand();
                    break;
                case 'vorwaerts':
                case 'abraeumen':
                    bewegung = { art: schritt, bis: jetzt + WAECHTER_BEWEGUNG_MS };
                    if (schritt === 'vorwaerts') u.verlauf.forward();
                    else u.verlauf.back();
                    planeAbgleich(WAECHTER_BEWEGUNG_MS);
                    break;
                case 'nichts':
                    break;
            }
        } catch {
            // Verlauf gesperrt oder gedrosselt (Safari: Obergrenze je zehn
            // Sekunden) — dann ohne Wächter, die Geste wirkt wie bisher.
            bewegung = null;
        }
    };

    const beiPopstate = (): void => {
        if (beendet) return;
        const vorher = letzter;
        const nachher = stand();
        letzter = nachher;
        gelandet = null;
        rueckschrittGesperrt = false;
        // Nach jeder Verlaufsbewegung zählt Chromium eine frühere Berührung
        // nicht mehr für neue Einträge.
        aktivierung = { ...aktivierung, verbraucht: true };
        const eigeneBewegung = bewegung !== null && u.jetzt() <= bewegung.bis;
        bewegung = null;
        try {
            if (!eigeneBewegung && waechterEntnommen(vorher, nachher, u.kennung)) {
                gelandet = nachher;
                // Ohne volle Oberfläche keine Entscheidung (siehe oben).
                // 'minimieren' tut hier nichts: die nächste Geste verlässt die Seite.
                u.oberflaeche()?.zurueck();
            }
        } finally {
            planeAbgleich(0);
        }
    };

    const beiBeruehrung = (ereignis: { readonly isTrusted?: boolean; readonly key?: string } = {}): void => {
        if (beendet || !zaehltAlsBeruehrung(ereignis)) return;
        aktivierung = { zeitMs: u.jetzt(), verbraucht: false };
        planeAbgleich(0);
    };

    const beenden = (): void => {
        beendet = true;
        if (zeitgeber !== null) u.abbrechen(zeitgeber);
        zeitgeber = null;
    };

    return { abgleichen: () => abgleich(false), beiPopstate, beiBeruehrung, beenden };
}
